import { createPortal } from 'react-dom';
import {
	Building2,
	Hash,
	Layers,
	LayoutGrid,
	Mail,
	Pencil,
	Plus,
	Users,
	X,
} from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';

const formatCPF = (value: string) => {
	const digits = value.replace(/\D/g, '');
	return digits
		.replace(/(\d{3})(\d)/, '$1.$2')
		.replace(/(\d{3})(\d)/, '$1.$2')
		.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

const employeeLabel = (employee: Employees.Employee) => {
	if (employee.user) {
		return `${employee.user.first_name ?? ''} ${
			employee.user.last_name ?? ''
		}`.trim();
	}
	return `CPF ${formatCPF(employee.cpf)}`;
};

const resolveSectorLabel = (
	employee: Employees.Employee,
	links: Companies.CompanyDepartmentLinkDetail[] | undefined,
): string => {
	if (employee.sector?.name) return employee.sector.name;
	if (!employee.sector_id || !employee.department_id || !links?.length) {
		return '—';
	}
	const row = links.find((l) => l.department?.id === employee.department_id);
	const name = row?.department?.company_sector?.find(
		(s) => s.id === employee.sector_id,
	)?.name;
	return name ?? '—';
};

const resolveDepartmentLabel = (
	employee: Employees.Employee,
	links: Companies.CompanyDepartmentLinkDetail[] | undefined,
	modalDepartmentName: string,
): string => {
	if (employee.department?.name) return employee.department.name;
	if (employee.department_id && links?.length) {
		const row = links.find(
			(l) => l.department?.id === employee.department_id,
		);
		if (row?.department?.name) return row.department.name;
	}
	return modalDepartmentName;
};

const memberText = 'text-xs font-semibold text-stone-600';
const rowClass = `flex items-center gap-2.5 ${memberText}`;
const rowIconClass = 'size-3.5 shrink-0 text-stone-400';

const memberInitials = (employee: Employees.Employee) => {
	if (employee.user) {
		const a = employee.user.first_name?.charAt(0) ?? '';
		const b = employee.user.last_name?.charAt(0) ?? '';
		const s = `${a}${b}`.trim();
		return s ? s.toUpperCase() : '?';
	}
	return '?';
};

type DepartmentMembersModalProps = {
	isOpen: boolean;
	onClose: () => void;
	departmentName: string;
	initials: string;
	iconClassName: string;
	members: Employees.Employee[];
	departmentLinks: Companies.CompanyDepartmentLinkDetail[] | undefined;
	/** Editar vínculo departamento/setor (mesmo fluxo da lista de colaboradores). */
	onRequestLinkOrg?: (employee: Employees.Employee) => void;
	/** Atalho para abrir modal de vínculo de colaborador. */
	onRequestLinkCollaborator?: () => void;
};

export const DepartmentMembersModal = ({
	isOpen,
	onClose,
	departmentName,
	initials,
	iconClassName,
	members,
	departmentLinks,
	onRequestLinkOrg,
	onRequestLinkCollaborator,
}: DepartmentMembersModalProps) => {
	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
				aria-hidden
			/>
			<div className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-[640px] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200">
				<div className="flex shrink-0 items-start justify-between gap-4 px-8 py-6">
					<div className="flex min-w-0 gap-4">
						<div
							className={cn(
								'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-sm font-black',
								iconClassName,
							)}
						>
							{initials}
						</div>
						<div className="min-w-0 space-y-1">
							<h2 className="text-lg font-black text-stone-900">
								{departmentName}
							</h2>
							<p className="text-sm text-stone-500">
								Colaboradores vinculados a este departamento
							</p>
						</div>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="shrink-0 text-stone-400 transition-colors hover:text-stone-600"
						aria-label="Fechar"
					>
						<X size={20} />
					</button>
				</div>

				<div className="flex-1 overflow-y-auto px-8 py-6">
					<div className="mb-4 flex items-center justify-between gap-3">
						<div className="flex items-center gap-2 text-stone-900">
							<LayoutGrid
								className="size-5 text-stone-500"
								strokeWidth={2}
							/>
							<span className="text-sm font-bold">
								Lista de colaboradores
							</span>
						</div>
						<button
							type="button"
							onClick={() => onRequestLinkCollaborator?.()}
							className="inline-flex items-center gap-1.5 rounded-full bg-black px-4 py-2 text-xs font-bold text-white shadow-md transition-colors hover:bg-neutral-800"
						>
							<Plus className="size-3.5" strokeWidth={2.5} />
							Vincular colaborador
						</button>
					</div>

					{members.length === 0 ? (
						<div className="flex flex-col items-center justify-center rounded-[2rem] border border-dashed border-stone-200 bg-stone-50/50 px-6 py-14 text-center">
							<div className="mb-4 rounded-full bg-white p-4 text-stone-300 shadow-sm">
								<Users className="size-8" strokeWidth={1.75} />
							</div>
							<h3 className="text-base font-bold text-stone-900">
								Nenhum colaborador neste departamento
							</h3>
							<p className="mx-auto mt-1 max-w-sm text-sm leading-relaxed text-stone-500">
								Vincule colaboradores a este departamento na aba
								Colaboradores da empresa.
							</p>
						</div>
					) : (
						<ul className="flex flex-col gap-4">
							{members.map((emp) => {
								const sectorName = resolveSectorLabel(
									emp,
									departmentLinks,
								);
								const deptName = resolveDepartmentLabel(
									emp,
									departmentLinks,
									departmentName,
								);
								const email = emp.user?.email?.trim() || '—';
								return (
									<li
										key={emp.id}
										className={cn(
											'relative flex flex-col gap-0 overflow-hidden rounded-2xl border border-stone-100 bg-white',
											'shadow-[0_1px_3px_rgba(15,23,42,0.06)] ring-1 ring-stone-100/80',
											'transition-all duration-200 hover:border-stone-200 hover:shadow-md hover:ring-stone-200/60',
										)}
									>
										{onRequestLinkOrg ? (
											<button
												type="button"
												onClick={(e) => {
													e.stopPropagation();
													onRequestLinkOrg(emp);
												}}
												className="absolute right-2 top-2 z-10 shrink-0 rounded-full p-2 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-900"
												title="Editar departamento e setor"
												aria-label="Editar vínculo do colaborador com departamento e setor"
											>
												<Pencil
													size={16}
													strokeWidth={2}
												/>
											</button>
										) : null}

										<div className="flex gap-3 p-4 pr-[4.75rem]">
											<div
												className={cn(
													'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-[11px] font-black uppercase tracking-tight',
													'bg-gradient-to-br from-stone-100 to-stone-50 text-stone-600 ring-1 ring-stone-200/60',
												)}
											>
												{memberInitials(emp)}
											</div>
											<div className="flex min-w-0 flex-1 flex-col gap-2.5">
												<p className="truncate text-sm font-bold leading-tight text-stone-900">
													{employeeLabel(emp)}
												</p>
												<div className="grid grid-cols-2 gap-x-5 gap-y-2.5 border-t border-stone-100 pt-3">
													<p className={rowClass}>
														<Mail
															className={
																rowIconClass
															}
															strokeWidth={2}
														/>
														<span className="min-w-0 truncate">
															{email}
														</span>
													</p>
													<p className={rowClass}>
														<Building2
															className={
																rowIconClass
															}
															strokeWidth={2}
														/>
														<span className="min-w-0 truncate">
															{deptName}
														</span>
													</p>
													<p className={rowClass}>
														<Hash
															className={
																rowIconClass
															}
															strokeWidth={2}
														/>
														<span className="min-w-0 truncate">
															{formatCPF(emp.cpf)}
														</span>
													</p>
													<p className={rowClass}>
														<Layers
															className={
																rowIconClass
															}
															strokeWidth={2}
														/>
														<span className="min-w-0 truncate">
															{sectorName}
														</span>
													</p>
												</div>
											</div>
										</div>
									</li>
								);
							})}
						</ul>
					)}
				</div>

				<div className="flex shrink-0 justify-end px-8 py-6">
					<Button
						type="button"
						text="Fechar"
						onClick={onClose}
						className="rounded-full bg-black px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-neutral-800"
					/>
				</div>
			</div>
		</div>,
		document.body,
	);
};
